import type { EmailDocumentSectionResponseDtoCategory } from 'src/apis/backend/generated/models';

import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useSectionsAccess } from '@/hooks/useSectionsAccess';
import { TrialLockedPanel } from '@/sections/white-glove/trial-locked-panel';
import {
  toSectionCategory,
  DashboardSectionsListView,
} from '@/sections/dashboard/sections/dashboard-sections-list';

import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/sections/')({
  component: SectionsPage,
  // Which kind of section the library opens on. A link from somewhere that
  // already knows the answer, such as the footer card on the dashboard, lands
  // on that shelf rather than on the whole library. The list owns the filter
  // once it is on screen.
  validateSearch: (
    search: Record<string, unknown>
  ): { category?: EmailDocumentSectionResponseDtoCategory } => ({
    category: toSectionCategory(search.category),
  }),
});

/**
 * The section library.
 *
 * Open to Base, managed and comped brands (see `useSectionsAccess`). A managed
 * brand needs it as much as a Base brand does: the footer our team builds for
 * it lands here, and the list is where it asks for one. There is no launch
 * flag of its own -- the brand's billing is the whole question.
 */
function SectionsPage() {
  const { t } = useTranslation('common');
  const { enabled: canUseSections, isLoading } = useSectionsAccess();
  const { category } = Route.useSearch();

  // Waited on rather than guessed. The closed answer and the unanswered one are
  // the same `false` from here, and showing an entitled brand the locked panel
  // for a beat tells it something untrue about what it has.
  if (isLoading) {
    return (
      <DashboardContent maxWidth="xl" sx={{ pt: { xs: 2, sm: 4 } }}>
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress size={28} />
        </Stack>
      </DashboardContent>
    );
  }

  if (!canUseSections) {
    return (
      <TrialLockedPanel
        title={t('nav.sections', 'Sections')}
        body={t(
          'sections.locked',
          'Sections are the building blocks you assemble an email from yourself.'
        )}
      />
    );
  }

  return <DashboardSectionsListView initialCategory={category} />;
}
